import React from "react";
import { Link } from "react-router-dom";
import Icon from "./ui/Icon";

const startingPoints = [
  {
    n: "01",
    icon: "grid",
    title: "Create an agent",
    text: "In Agent Hub, choose Create agent. Name it, choose a model, and write its instructions. Tools and scheduling are optional. Select Create Agent to open a conversation.",
    url: "/agent-hub/create",
    action: "Create an agent",
  },
  {
    n: "02",
    icon: "chat",
    title: "Try a conversation",
    text: "Open an agent from My agents or Agent library and send a message. Use Edit to adjust its instructions, then try the conversation again.",
    url: "/agent-hub",
    action: "Open Agent Hub",
  },
  {
    n: "03",
    icon: "activity",
    title: "Build a workflow",
    text: "Add available agents to the canvas, connect their steps, and choose a trigger. Select Save workflow before Run, then open Run history to see the result.",
    url: "/workflows",
    action: "Open Workflows",
  },
  {
    n: "04",
    icon: "spark",
    title: "Shape an app",
    text: "Open an application, or choose Create an app and describe your idea. Use the App assistant’s text chat to add records or update its dashboard.",
    url: "/apps",
    action: "Explore Apps",
  },
];

export default function About() {
  const walkthrough = `${import.meta.env.BASE_URL}video/workspace-walkthrough-2026-09`;
  const videoUrl = `${walkthrough}.mp4?v=5897061`;

  return (
    <div className="ui-page about-page">
      <div className="ui-page-heading">
        <span className="ui-eyebrow">A SMALL START. A USEFUL NEXT STEP.</span>
        <h1>Build a little help into your day.</h1>
        <p>
          Create an agent, try an idea in conversation, or make room for a new
          workflow or app. Here’s how to get started in KH Workspace.
        </p>
      </div>

      <section className="about-demo" aria-labelledby="workspace-demo-title">
        <div className="about-demo-heading">
          <div>
            <span className="ui-eyebrow">THE CURRENT WORKSPACE</span>
            <h2 id="workspace-demo-title">See how it comes together.</h2>
            <p id="workspace-demo-description">
              Follow the current screens: create an agent, draft a message,
              save a manual workflow, and add a record in an app.
            </p>
          </div>
          <Link className="ui-button ui-button--secondary" to="/agent-hub">
            Open Agent Hub <Icon name="arrow" size={17} />
          </Link>
        </div>
        <figure className="about-demo-player">
          <video
            controls
            playsInline
            preload="metadata"
            poster={`${walkthrough}.jpg`}
            aria-labelledby="workspace-demo-title"
            aria-describedby="workspace-demo-description"
          >
            <source src={videoUrl} type="video/mp4" />
            <track
              kind="captions"
              src={`${walkthrough}.vtt`}
              srcLang="en"
              label="English"
              default
            />
            Your browser does not support embedded video. {" "}
            <a href={videoUrl}>Open the workspace walkthrough.</a>
          </video>
          <figcaption>
            <span>Recorded in the live workspace · September 2026 · Captions available</span>
            <a className="ui-text-link" href={videoUrl}>
              Open video <Icon name="arrowUpRight" size={16} />
            </a>
          </figcaption>
        </figure>
        <p className="about-demo-note">
          This tour shows agent setup and real workflow and app saves.
          AI replies and workflow execution are not demonstrated in the video.
          For agent conversations, manage your model connections in{" "}
          <Link className="ui-text-link" to="/settings">Workspace settings</Link>.
        </p>
      </section>

      <section className="about-start" aria-labelledby="workspace-start-title">
        <div className="about-start-heading">
          <span className="ui-eyebrow">YOUR NEXT STEP</span>
          <h2 id="workspace-start-title">Find your starting point.</h2>
          <p>Choose the task you need today. Each area has its own place in the workspace.</p>
        </div>
        <div className="about-steps">
          {startingPoints.map((step) => (
            <section className="ui-panel" key={step.n} aria-labelledby={`workspace-step-${step.n}`}>
              <div className="about-step-top">
                <span className="about-step-icon">
                  <Icon name={step.icon} size={23} />
                </span>
                <span aria-hidden="true">{step.n}</span>
              </div>
              <h3 id={`workspace-step-${step.n}`}>{step.title}</h3>
              <p>{step.text}</p>
              <Link className="ui-text-link" to={step.url}>
                {step.action} <Icon name="arrow" size={17} />
              </Link>
            </section>
          ))}
        </div>
      </section>

      <div className="about-founder">
        <span>Built by Kangrong Hu, with a curiosity for making AI useful.</span>
        <Link className="ui-text-link" to="/about/founder">
          Meet the builder <Icon name="arrowUpRight" size={17} />
        </Link>
      </div>
    </div>
  );
}
